<template>
  <modal-window :show="show" @close="closeModal">
    <template v-slot:popup-title>Расчет лизинга</template>
    <template v-slot:popup-content>
      <div v-if="isPrefillLoading" class="text-center position-relative leasing-loading">
        <Preloader invert />
      </div>
      <p v-else-if="loadError" class="text-center form-error">{{ loadError }}</p>
      <Transition v-else name="success">
        <form
          v-if="!isSuccess"
          key="1"
          @submit.prevent="sendLeasing"
          class="form"
        >
          <p class="form-section-title">Запросить расчет лизинга на авто</p>
          <div class="form-field">
            <p>Марка, модель</p>
            <input class="input" :value="carName" type="text" disabled />
          </div>
          <div class="form-field">
            <p>VIN</p>
            <input class="input" :value="lot.vin" type="text" disabled />
          </div>
          <div class="form-field">
            <p>Год выпуска</p>
            <input class="input" :value="lot.year" type="text" disabled />
          </div>
          <div class="form-field">
            <p>Пробег</p>
            <input class="input" :value="lot.mileage" type="text" disabled />
          </div>
          <div class="form-field">
            <p>Местонахождение</p>
            <input class="input" :value="lot.city" type="text" disabled />
          </div>
          <div v-if="isCompany" class="form-field">
            <p>Стоимость</p>
            <input class="input" :value="price" type="text" disabled />
          </div>

          <template v-if="isCompany">
            <p class="form-section-title">Данные о компании</p>
            <div class="form-field">
              <p>Название юр. лица</p>
              <input
                class="input"
                v-model="form.companyName"
                placeholder="Название юр. лица"
                type="text"
                maxlength="255"
              />
              <p class="form-error" v-if="errors.companyName">{{ errors.companyName }}</p>
            </div>
            <div class="form-field">
              <p>ИНН организации</p>
              <input
                class="input"
                v-model="form.inn"
                placeholder="ИНН"
                type="text"
                inputmode="numeric"
                :maxlength="innLength"
                @input="form.inn = removeNonDigits(form.inn)"
              />
              <p class="form-error" v-if="errors.inn">{{ errors.inn }}</p>
            </div>
          </template>
          <p v-else class="form-section-title">Данные о физическом лице</p>

          <div class="form-field">
            <p>Имя</p>
            <input class="input" :value="userData.contactName" type="text" disabled />
          </div>
          <div class="form-field">
            <p>Телефон</p>
            <input class="input" :value="userData.phone" type="text" disabled />
          </div>

          <p class="form-error text-center" v-if="errors.common">{{ errors.common }}</p>

          <div
            class="text-center position-relative"
            :class="{ '--loading': isLoading }"
          >
            <button
              class="button --compact"
              type="submit"
              :class="{ '--loading': isLoading }"
            >
              Отправить
            </button>
            <Preloader v-if="isLoading" class="for-button" invert />
          </div>
        </form>
        <div v-else key="2" class="text-center">
          <p>Заявка на&nbsp;расчет лизинга успешно отправлена</p>
        </div>
      </Transition>
    </template>
  </modal-window>
</template>

<script setup lang="ts">
import ModalWindow from "~/components/page/modalWindow.vue";
import Preloader from "~/components/page/Preloader.vue";
import { USER_TYPE } from "~/@types";
import { removeNonDigits } from "~/utils/validations";

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
});

const show = ref(false);
const isPrefillLoading = ref(false);
const loadError = ref("");
const isSuccess = ref(false);
const isLoading = ref(false);

const lot = ref<Record<string, any>>({});
const userData = ref<Record<string, any>>({});

const form = ref({
  companyName: "",
  inn: "",
});

const errors = ref({
  companyName: "",
  inn: "",
  common: "",
});

const isCompany = computed(() => userData.value.type !== USER_TYPE.PHYSICAL);
const innLength = computed(() => (userData.value.type === USER_TYPE.INDIVIDUAL ? 12 : 10));

// Название модели обычно уже содержит марку («Chery Tiggo») — марку не задваиваем
const carName = computed(() => {
  const brand = (lot.value.brand || "").trim();
  const model = (lot.value.model || "").trim();

  if (!brand || model.toLowerCase().startsWith(brand.toLowerCase())) {
    return model;
  }

  return `${brand} ${model}`.trim();
});

const price = computed(() =>
  lot.value.price == null ? "" : `${new Intl.NumberFormat("ru-RU").format(lot.value.price)} ₽`
);

const resetErrors = () => {
  errors.value = { companyName: "", inn: "", common: "" };
};

const loadPrefill = async () => {
  isPrefillLoading.value = true;
  loadError.value = "";

  const apiRequest = new ApiRequest("get", `lots/deal/${props.item.id}/leasing/prefill`);

  apiRequest.onSuccess((data) => {
    lot.value = data.lot;
    userData.value = data.user;
    form.value.companyName = data.user.companyName || "";
    form.value.inn = data.user.inn || "";
  });

  apiRequest.onError(() => {
    loadError.value = "Не удалось загрузить данные для заявки";
  });

  await apiRequest.request();
  isPrefillLoading.value = false;
};

const showModal = () => {
  isSuccess.value = false;
  resetErrors();
  show.value = true;
  loadPrefill();
};

const closeModal = () => {
  show.value = false;
};

const validateForm = () => {
  resetErrors();

  if (!isCompany.value) {
    return true;
  }

  let isValid = true;

  if (!form.value.companyName.trim()) {
    errors.value.companyName = "Заполните название юр. лица";
    isValid = false;
  }

  if (!form.value.inn) {
    errors.value.inn = "Заполните ИНН";
    isValid = false;
  } else if (form.value.inn.length !== innLength.value) {
    errors.value.inn = `ИНН должен состоять из ${innLength.value} цифр`;
    isValid = false;
  }

  return isValid;
};

const sendLeasing = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.value = true;

  const apiRequest = new ApiRequest(
    "post",
    `lots/deal/${props.item.id}/leasing`,
    null,
    isCompany.value
      ? { companyName: form.value.companyName.trim(), inn: form.value.inn }
      : {}
  );

  apiRequest.onSuccess(() => {
    isSuccess.value = true;
  });

  apiRequest.onValidationError((error) => {
    errors.value.companyName = error.errors?.companyName?.[0] ?? "";
    errors.value.inn = error.errors?.inn?.[0] ?? "";
  });

  apiRequest.onError((error) => {
    if (!errors.value.companyName && !errors.value.inn) {
      errors.value.common = error.message || "Произошла ошибка при отправке заявки";
    }
  });

  await apiRequest.request();
  isLoading.value = false;
};

defineExpose({
  showModal,
});
</script>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
}

.form-section-title {
  font-weight: 600;
  margin: 8px 0 12px;
}

.leasing-loading {
  min-height: 80px;
}
</style>
